import _ from 'lodash';
import {
    DeliveryStrategyKind,
    FlowStateWithRelationsDto,
    NotificationStatus,
    NotificationWithConversationContextDto,
} from '@ampsec/platform-client';
import {
    ColumnApiModule,
    CsvExportModule,
    ModuleRegistry,
    RenderApiModule,
    TextFilterModule,
    TooltipModule,
    ValidationModule,
    ColDef,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    RowClickedEvent,
} from 'ag-grid-community';
import {
    ColumnsToolPanelModule,
    ColumnMenuModule,
    ContextMenuModule,
    LicenseManager,
    ServerSideRowModelModule,
    SetFilterModule,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { getEngagementLogs } from 'Apis/library';
import { Typography } from 'Components/atoms';
import { AgGridTableHeader } from 'Components/molecules';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    COLUMN_STATE_STORAGE_KEYS,
    DEFAULT_COLUMN_STATE,
    DEFAULT_COL_DEF,
    GRID_CONFIG,
    findingsTheme,
    AgGridEngagementLogsProps,
    COLUMN_MIN_WIDTHS,
} from './AgGridEngagementLogs.config';
import { Link } from 'react-router-dom';
import { DEFAULT_WORKFLOW_ROUTE } from 'Core-utils/routes';
import { EngagementLogDetailModal } from '../EnagementLogDetailModal';
import { Badge } from 'react-bootstrap';
import moment from 'moment';
import { EmptyState } from 'Components/molecules/EmptyState';
import EngageIcon from 'Assets/icons/engagement.svg';
import { useI18n } from 'Hooks/useI18n';
import { DateTimeFormatter } from 'Core-utils/helpers/helpers';
import {
    getFlowStateCreatedTimestamp,
    getFlowStateUpdatedTimestamp,
} from '../EnagementLogDetailModal/engagementLogHistoryDisplay';
import './styles.scss';

ModuleRegistry.registerModules([
    ServerSideRowModelModule,
    TextFilterModule,
    TooltipModule,
    SetFilterModule,
    ColumnApiModule,
    RenderApiModule,
    ValidationModule,
    CsvExportModule,
    ColumnsToolPanelModule,
    ColumnMenuModule,
    ContextMenuModule,
]);

const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
if (agGridLicenseKey) {
    LicenseManager.setLicenseKey(agGridLicenseKey);
}

const formatEngagementLogDate = (dateString: string | undefined): string => {
    if (!dateString) {
        return '-';
    }
    const formatted = DateTimeFormatter(dateString);
    return formatted || '-';
};

const getStatusBadgeClass = (status: NotificationStatus): string => {
    switch (status) {
        case NotificationStatus.SENT:
            return 'badge-success';
        case NotificationStatus.PENDING:
            return 'badge-warning';
        case NotificationStatus.READ:
            return 'badge-info';
        case NotificationStatus.ERROR:
        case NotificationStatus.INVALID:
            return 'badge-danger';
        default:
            return 'badge-secondary';
    }
};

const getDeliveryChannelDisplayContent = (
    deliveryChannel: DeliveryStrategyKind,
    status: NotificationStatus,
): React.ReactNode => {
    switch (deliveryChannel) {
        case DeliveryStrategyKind.SLACK:
            return <Badge className={getStatusBadgeClass(status)}>Slack</Badge>;
        case DeliveryStrategyKind.TEAMS:
            return <Badge className={getStatusBadgeClass(status)}>Teams</Badge>;
        case DeliveryStrategyKind.EMAIL:
            return <Badge className={getStatusBadgeClass(status)}>Email</Badge>;
        default:
            return deliveryChannel;
    }
};

const AgGridEngagementLogs: React.FC<AgGridEngagementLogsProps> = ({ title, defaultFilter, tableBodyHeight }) => {
    const gridRef = useRef<any>(null);
    const columnStateLoadedRef = useRef(false);
    const { t } = useI18n();
    const [filteredCount, setFilteredCount] = useState<number | null>(null);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [selectedFlowState, setSelectedFlowState] = useState<FlowStateWithRelationsDto | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                field: 'updatedAt',
                headerName: 'Updated At',
                colId: 'updatedAt',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                valueGetter: (params: any) => getFlowStateUpdatedTimestamp(params.data),
                cellRenderer: (params: any) => formatEngagementLogDate(params.value),
                valueFormatter: (params: any) => formatEngagementLogDate(params.value),
            },
            {
                field: 'id',
                headerName: 'Flow State ID',
                colId: 'id',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                sort: 'desc' as const,
                cellRenderer: (params: any) => (params.value ? params.value : '-'),
            },
            {
                field: 'user',
                headerName: 'User',
                colId: 'user',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const user = params.data?.user || [];
                    return user.firstName + ' ' + user.lastName;
                },
            },
            {
                field: 'userDepartment',
                headerName: 'Department',
                colId: 'userDepartment',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const user = params.data?.user || [];
                    return user.department;
                },
            },
            {
                field: 'assetName',
                headerName: 'Asset Name',
                colId: 'assetName',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const asset = params.data?.asset || [];
                    return asset.displayValue;
                },
            },
            {
                field: 'findingKind',
                headerName: 'Finding Kind',
                colId: 'findingKind',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.MEDIUM_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const finding = params.data?.finding || [];
                    return finding.kind;
                },
            },
            {
                field: 'findingDescription',
                headerName: 'Finding Description',
                colId: 'findingDescription',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.LONG_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const finding = params.data?.finding || [];
                    return finding.description;
                },
            },
            {
                field: 'findingStatus',
                headerName: 'Finding Status',
                colId: 'findingStatus',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const finding = params.data?.finding || [];
                    return finding.status;
                },
            },
            {
                field: 'findingSeverity',
                headerName: 'Finding Severity',
                colId: 'findingSeverity',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const finding = params.data?.finding || [];
                    return finding.severity;
                },
            },
            {
                field: 'findingAge',
                headerName: 'Finding Age',
                colId: 'findingAge',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const finding = params.data?.finding || [];
                    return moment.duration(moment().diff(finding.createdAt)).humanize();
                },
            },
            {
                field: 'vulnerabilityAffectedApp',
                headerName: 'Affected App',
                colId: 'vulnerabilityAffectedApp',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.MEDIUM_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const saasComponent = params.data?.saasComponent || {};
                    const hasRawApps = !_.isEmpty(saasComponent?.meta?._raw?.apps);
                    const hasVulnApp = !_.isEmpty(saasComponent?.meta?._vulnerability?.app);
                    if (hasVulnApp) {
                        return saasComponent?.meta?._raw?._vulnerability?.app[0]?.name;
                    }
                    if (hasRawApps) {
                        return saasComponent?.meta?._raw?.apps[0]?.product_name_version;
                    }
                    return '-';
                },
            },
            {
                field: 'vulnerabilityRemediation',
                headerName: 'Remediation',
                colId: 'vulnerabilityRemediation',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.LONG_TEXT,
                cellRenderer: (params: any) => params.value,
                valueGetter: (params: any) => {
                    const hasRawRemediation = !_.isEmpty(params.data?.saasComponent?.meta?._raw?.remediation?.entities);
                    const hasVulnerabiltiyPatch = !_.isEmpty(params.data?.saasComponent?.meta?._vulnerability?.PATCH);
                    if (hasRawRemediation) {
                        return params.data?.saasComponent?.meta?._raw?.remediation?.entities[0].action;
                    }
                    if (hasVulnerabiltiyPatch) {
                        return params.data?.saasComponent?.meta?._raw?._vulnerability?.PATCH;
                    }
                    return '-';
                },
            },
            {
                field: 'baseMessage',
                headerName: 'Base Message',
                colId: 'baseMessage',
                flex: 3,
                minWidth: COLUMN_MIN_WIDTHS.EXTRA_LONG_TEXT,
                cellRenderer: (params: any) => params.value,
                valueGetter: (params: any) => {
                    const notifications = params.data?.notifications || [];
                    return notifications
                        .map(
                            (notification: NotificationWithConversationContextDto) =>
                                notification.conversationContext?.context,
                        )
                        .join(', ');
                },
            },
            {
                field: 'engagementCreatedAt',
                headerName: 'Started At',
                colId: 'engagementCreatedAt',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                valueGetter: (params: any) => getFlowStateCreatedTimestamp(params.data),
                cellRenderer: (params: any) => formatEngagementLogDate(params.value),
                valueFormatter: (params: any) => formatEngagementLogDate(params.value),
            },
            {
                field: 'channel',
                headerName: 'Channel',
                colId: 'channel',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.MINI_TEXT,
                valueGetter: (params: any) => {
                    const notifications = params.data?.notifications || [];
                    const channels = notifications
                        .map((notification: NotificationWithConversationContextDto) => notification.deliveryChannel)
                        .filter(Boolean);
                    return channels.length > 0 ? channels.join(', ') : '-';
                },
                cellRenderer: (params: any) => {
                    const value = params.data?.notifications || [];
                    const result = value.map((notification: NotificationWithConversationContextDto) => {
                        return {
                            deliveryChannel: notification.deliveryChannel,
                            status: notification.status,
                        };
                    });
                    return result
                        ? result.map((item: any) => getDeliveryChannelDisplayContent(item.deliveryChannel, item.status))
                        : '-';
                },
            },
            {
                headerName: 'Engagement',
                colId: 'engagement',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => {
                    const value = params.data?.specId || '-';
                    return value ? (
                        <Link to={`${DEFAULT_WORKFLOW_ROUTE}/create-engagement/${value}`}>{value}</Link>
                    ) : (
                        '-'
                    );
                },
            },
            {
                field: 'status',
                headerName: 'Engagement Status',
                colId: 'engagementStatus',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                valueGetter: (params: any) => params.data?.status || '-',
                cellRenderer: (params: any) => {
                    const value = params.data?.status || [];
                    return value ? value : '-';
                },
            },
            {
                field: 'history',
                headerName: 'Engagement Step',
                colId: 'engagementStep',
                flex: 0.5,
                minWidth: COLUMN_MIN_WIDTHS.SHORT_TEXT,
                cellRenderer: (params: any) => params.value || '-',
                valueGetter: (params: any) => {
                    const flowStateHistorySize = params.data?.history?.length || 0;
                    const lastHistoryRecord = params.data?.history[flowStateHistorySize - 1];
                    return lastHistoryRecord ? lastHistoryRecord.nodeName : '-';
                },
            },
        ],
        [],
    );

    const createServerSideDatasource = useCallback((): IServerSideDatasource => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                const { startRow, endRow, sortModel } = params.request;
                const limit = endRow! - startRow!;
                const offset = startRow!;

                // Build filter criteria
                const filter: Record<string, any> = {
                    limit,
                    offset,
                    ...defaultFilter,
                };

                // Handle sorting
                if (sortModel && sortModel.length > 0) {
                    const sortField = sortModel[0].colId === 'lastUpdated' ? 'updatedAt' : sortModel[0].colId;
                    const sortDirection = sortModel[0].sort?.toUpperCase() || 'DESC';
                    filter.sort = { [sortField]: sortDirection };
                } else {
                    filter.sort = { updatedAt: 'DESC' };
                }

                try {
                    const response = await getEngagementLogs(filter);

                    if (!response) {
                        params.fail();
                        return;
                    }

                    const rowData = response.data || [];
                    const rowCount = response?.hints?.count ?? rowData.length;

                    setFilteredCount(rowData.length);
                    setTotalCount(rowCount);

                    params.success({
                        rowData,
                        rowCount,
                    });

                    // Manually control overlay visibility for server-side row model
                    if (rowData.length === 0) {
                        gridRef.current?.api?.showNoRowsOverlay();
                    } else {
                        gridRef.current?.api?.hideOverlay();
                    }
                } catch (error) {
                    params.fail();
                }
            },
        };
    }, [defaultFilter]);

    const datasource = useMemo(() => createServerSideDatasource(), [createServerSideDatasource]);

    const onGridReady = useCallback((params: any) => {
        const savedState = localStorage.getItem(COLUMN_STATE_STORAGE_KEYS.server);
        if (savedState) {
            try {
                params.api.applyColumnState({ state: JSON.parse(savedState), applyOrder: true });
            } catch {
                params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
            }
        } else {
            params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
        }
        columnStateLoadedRef.current = true;
    }, []);

    const handleColumnEvent = useCallback((params: any) => {
        if (!params || !params.api || !columnStateLoadedRef.current) {
            return;
        }
        if (params.type === 'columnMoved' || params.type === 'columnVisible' || params.finished) {
            const state = params.api.getColumnState();
            localStorage.setItem(COLUMN_STATE_STORAGE_KEYS.server, JSON.stringify(state));
        }
    }, []);

    const handleResetColumns = useCallback(() => {
        const gridApi = gridRef.current?.api;
        if (gridApi) {
            gridApi.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
            const resetState = gridApi.getColumnState();
            localStorage.setItem(COLUMN_STATE_STORAGE_KEYS.server, JSON.stringify(resetState));
            gridApi.setFilterModel({});
        }
    }, []);

    const handleExportCsv = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.exportDataAsCsv();
        }
    }, []);

    const handleRowClicked = useCallback((event: RowClickedEvent<FlowStateWithRelationsDto>) => {
        if (event.data) {
            setSelectedFlowState(event.data);
            setIsModalOpen(true);
        }
    }, []);

    const handleModalClose = useCallback(() => {
        setIsModalOpen(false);
        setSelectedFlowState(null);
    }, []);

    return (
        <div className="h-100 d-flex flex-column user-notifications__container">
            <AgGridTableHeader
                title={title}
                rightExtras={
                    typeof filteredCount === 'number' && typeof totalCount === 'number' ? (
                        <Typography
                            variant="body10"
                            color="text-low-emphasis"
                            className="ag-grid-table-header__results-count"
                        >
                            <span className="ag-grid-table-header__results-count-number">{filteredCount}</span> results
                            of <span className="ag-grid-table-header__results-count-number">{totalCount}</span> total
                        </Typography>
                    ) : null
                }
                actionItems={[
                    { label: 'Export CSV', onClick: handleExportCsv },
                    { label: 'Reset Columns', onClick: handleResetColumns },
                ]}
            />
            <div
                className="ag-theme-alpine flex-grow-1 engagement-logs__grid-wrapper engagement-logs__grid-wrapper--theme-findings ag-grid-theme--amp-dark"
                style={{
                    height: tableBodyHeight || GRID_CONFIG.defaultHeight,
                    minHeight: GRID_CONFIG.minHeight,
                }}
            >
                <AgGridReact
                    ref={gridRef}
                    theme={findingsTheme}
                    columnDefs={columnDefs}
                    rowModelType="serverSide"
                    serverSideDatasource={datasource}
                    domLayout="normal"
                    suppressAggFuncInHeader={true}
                    suppressColumnVirtualisation={false}
                    suppressHorizontalScroll={false}
                    suppressContextMenu={true}
                    animateRows={true}
                    defaultColDef={DEFAULT_COL_DEF}
                    rowHeight={GRID_CONFIG.rowHeight}
                    headerHeight={GRID_CONFIG.headerHeight}
                    cacheBlockSize={GRID_CONFIG.cacheBlockSize}
                    onGridReady={onGridReady}
                    onColumnResized={handleColumnEvent}
                    onColumnMoved={handleColumnEvent}
                    onColumnVisible={handleColumnEvent}
                    onColumnPinned={handleColumnEvent}
                    onRowClicked={handleRowClicked}
                    sideBar={GRID_CONFIG.sideBar}
                    noRowsOverlayComponent={EmptyState}
                    noRowsOverlayComponentParams={{
                        ImageSrc: EngageIcon,
                        noDescription: false,
                        description: t('ENGAGEMENT_LOG_STATE_NO_DATA_DESCRIPTION'),
                    }}
                />
            </div>
            <EngagementLogDetailModal show={isModalOpen} onClose={handleModalClose} flowState={selectedFlowState} />
        </div>
    );
};

export default AgGridEngagementLogs;
